import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    IconButton,
    Grid,
    Divider,
    Tooltip,
    Badge,
    Paper
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GavelIcon from '@mui/icons-material/Gavel';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// MUI DatePicker imports
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';

const HearingDetails = ({
    todayHearingList = [],
    upcomingHearings = [],
    companyLogo,
    companyName,
    calendarData = [], // Pass API response data array here
    onMonthChange,     // Function trigger when month changes: (payload) => fetchApi(payload)
    onDateSelect       // Optional: Callback when user clicks a specific date
}) => {
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const { user_id, company_id, } = useSelector((state) => state.GlobalVariables);

    // Transform calendar API response array into Map for quick lookup: { "2026-07-02": 1, "2026-07-28": 1 }
    const hearingCountsMap = React.useMemo(() => {
        const map = {};
        if (Array.isArray(calendarData)) {
            calendarData.forEach(item => {
                if (item.hearing_date) {
                    map[item.hearing_date] = item.total_hearings;
                }
            });
        }
        return map;
    }, [calendarData]);

    // Handle Month/Year Change on Calendar
    const handleMonthChange = (newDate) => {
        const payload = {
            company_id: company_id, // Pass dynamic company_id if needed
            month: newDate.month() + 1, // dayjs months are 0-indexed (0=Jan, 6=July)
            year: newDate.year()
        };

        if (onMonthChange) {
            onMonthChange(payload);
        }
    };

    const ServerDay = (props) => {
        const { day, outsideCurrentMonth, ...other } = props;
        const dateStr = day.format('YYYY-MM-DD');
        const count = hearingCountsMap[dateStr] || 0;
        const hasHearing = !outsideCurrentMonth && count > 0;

        return (
            <Badge
                key={props.day.toString()}
                overlap="circular"
                badgeContent={hasHearing ? count : undefined}
                sx={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    '& .MuiBadge-badge': {
                        fontSize: { xs: '0.6rem', sm: '0.7rem' },
                        fontWeight: 700,
                        // height: { xs: 16, sm: 20 },
                        // minWidth: { xs: 16, sm: 20 },
                        padding: '0 4px',
                        top: '6px',
                        right: '6px',
                        zIndex: 2,
                        pointerEvents: 'none', // Ensures click goes through to the day cell
                        // Modern Gradient + Glow Effect
                        background: 'linear-gradient(135deg, #ff416c 0%, #ff4b2b 100%)',
                        color: '#ffffff',
                        boxShadow: '0 2px 6px rgba(255, 65, 108, 0.4)',
                        border: '1.5px solid #ffffff',
                    }
                }}
            >
                <PickersDay
                    {...other}
                    outsideCurrentMonth={outsideCurrentMonth}
                    day={day}
                    sx={{
                        // Converting standard round buttons to Responsive Grid Boxes
                        width: '100% !important',
                        // height: { xs: 38, sm: 46, md: 50 }, // Height scales dynamically per device
                        // maxWidth: '100%',
                        margin: '2px !important',
                        borderRadius: 2, // Rounded Square Grid Style
                        fontSize: { xs: '0.8rem', sm: '0.9rem' },
                        transition: 'all 0.2s ease-in-out',

                        // Default day hover effect
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            borderRadius: 2,
                        },

                        // Highlight dates having hearings
                        ...(hasHearing && {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                            fontWeight: 700,
                            color: 'primary.main',
                            borderRadius: 2, // Keeps the square grid shape instead of 50% circle
                            border: '1.5px solid #1976d2',
                            boxShadow: 'inset 0 0 0 1px #1976d2',
                            '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.18)',
                            }
                        })
                    }}
                />
            </Badge>
        );
    };

    // PDF Export Functionality

    const handleDownloadPDF = (data, title) => {
        if (!data || data.length === 0) {
            alert("No data available to download");
            return;
        }
        console.log("Preparing to download PDF for:", title, "with", data.length, "records");

        const doc = new jsPDF("landscape", "mm", "a4");
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        let currentY = 15;

        // --- COLOR PALETTE (Professional Corporate Theme) ---
        const PRIMARY_COLOR = [24, 43, 73];       // Deep Navy
        const ACCENT_COLOR = [52, 73, 94];        // Slate Gray
        const TEXT_MAIN = [33, 37, 41];          // Dark Charcoal
        const TEXT_MUTED = [108, 117, 125];      // Muted Gray
        const BG_LIGHT = [248, 249, 250];        // Off-White

        // ==========================================
        // 1. COMPANY HEADER & BRANDING
        // ==========================================
        const logoWidth = 18;
        const logoHeight = 18;
        let headerTextX = 14;

        if (companyLogo) {
            try {
                doc.addImage(companyLogo, "PNG", 14, currentY, logoWidth, logoHeight);
                headerTextX = 36; // Logo ke baad gap
            } catch (e) {
                console.warn("Logo rendering failed:", e);
            }
        }

        // Company Name
        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        doc.setTextColor(...PRIMARY_COLOR);
        doc.text(companyName || "Legal Management System", headerTextX, currentY + 7);

        // Company Subtitle/Tagline
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(...TEXT_MUTED);
        // doc.text("Automated Case Tracking & Reports", headerTextX, currentY + 13);

        // Header Right: Generation Meta Info
        const today = new Date().toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });

        doc.setFontSize(9);
        doc.setTextColor(...TEXT_MUTED);
        doc.text(`Generated: ${today}`, pageWidth - 14, currentY + 7, { align: "right" });
        doc.text("Status: Confidential", pageWidth - 14, currentY + 13, { align: "right" });

        currentY += 22;

        // Horizontal Divider Line
        doc.setDrawColor(220, 224, 230);
        doc.setLineWidth(0.5);
        doc.line(14, currentY, pageWidth - 14, currentY);

        currentY += 10;

        // ==========================================
        // 2. REPORT TITLE & SUMMARY BAR
        // ==========================================
        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.setTextColor(...PRIMARY_COLOR);
        doc.text(title, pageWidth / 2, currentY, {
            align: "center",
        });

        // Total Count Badge/Text
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(...ACCENT_COLOR);
        doc.text(`Total Records: ${data.length}`, pageWidth - 14, currentY, { align: "right" });

        currentY += 6;

        // ==========================================
        // 3. TABLE DATA PREPARATION
        // ==========================================
        const tableHeaders = [
            "S.No",
            "Case Type",
            "Case No",
            "Party Name",
            "Opposite Party",
            "Prev. Hearing",
            "Hearing Date",
            "Court (Room)",
            "Stage",
            "Judge Name",
        ];

        const tableRows = data.map((item, index) => [
            index + 1,
            item.case_type_name || "-",
            item.case_no || "-",
            item.client_names?.length ? item.client_names.join(", ") : "-",
            item.opposite_party || "-",
            item.hearing_date || "-",
            item.next_hearing_date || "-",
            `${item.court_name || "-"}${item.court_room ? ` (${item.court_room})` : ""}`,
            item.stage_name || "-",
            item.judge_name || "-",
        ]);

        // ==========================================
        // 4. AUTOTABLE GENERATION
        // ==========================================
        doc.autoTable({
            startY: currentY,
            head: [tableHeaders],
            body: tableRows,
            theme: "plain",

            margin: { top: 20, right: 14, bottom: 18, left: 14 },

            styles: {
                font: "helvetica",
                fontSize: 8,
                cellPadding: 2.5,
                textColor: TEXT_MAIN,
                valign: "middle",
                overflow: "linebreak",
            },

            headStyles: {
                fillColor: PRIMARY_COLOR,
                textColor: [255, 255, 255],
                fontStyle: "bold",
                fontSize: 8.5,
                halign: "left",
                cellPadding: 3,
            },

            bodyStyles: {
                lineColor: [235, 238, 242],
                lineWidth: 0.1,
            },

            alternateRowStyles: {
                fillColor: BG_LIGHT,
            },

            // Auto column layout with explicit alignments
            columnStyles: {
                0: { cellWidth: 10, halign: "center" }, // S.No
                1: { cellWidth: 22 },                   // Case Type
                2: { cellWidth: 24, fontStyle: "bold" }, // Case No
                3: { cellWidth: 38 },                   // Party Name
                4: { cellWidth: 35 },                   // Opposite Party
                5: { cellWidth: 22, halign: "center" }, // Prev. Hearing
                6: { cellWidth: 22, halign: "center" }, // Next Hearing
                7: { cellWidth: 32 },                   // Court
                8: { cellWidth: 22 },                   // Stage
                9: { cellWidth: "auto" },               // Judge Name (takes remaining space)
            },

            // Header and Footer for every page
            didDrawPage: function (data) {
                const totalPages = doc.internal.getNumberOfPages();
                const currentPage = doc.internal.getCurrentPageInfo().pageNumber;

                // Page Footer Line
                doc.setDrawColor(220, 224, 230);
                doc.setLineWidth(0.4);
                doc.line(14, pageHeight - 12, pageWidth - 14, pageHeight - 12);

                // Footer Text
                doc.setFontSize(8);
                doc.setFont("helvetica", "normal");
                doc.setTextColor(...TEXT_MUTED);

                // Left: Legal notice
                doc.text("Confidential — Legal Management System Report", 14, pageHeight - 6);

                // Right: Page Numbers
                doc.text(`Page ${currentPage} of ${totalPages}`, pageWidth - 14, pageHeight - 6, {
                    align: "right",
                });
            },
        });

        // ==========================================
        // 5. SAVE FILE
        // ==========================================
        const fileName = `${title.trim().toLowerCase().replace(/\s+/g, "_")}_report_${today.replace(/ /g, "_")}.pdf`;
        doc.save(fileName);
    };

    // Notice Board UI
    const NoticeBoard = ({ title, data, isToday }) => {
        return (
            <Box
                sx={{
                    width: '100%',
                    backgroundColor: '#f8f9fa',
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    boxShadow: '0px 4px 10px rgba(0,0,0,0.05)'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: 2,
                        backgroundColor: '#1976d2',
                        color: 'white',
                        borderTopLeftRadius: 7,
                        borderTopRightRadius: 7
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isToday ? <GavelIcon /> : <CalendarMonthIcon />}
                        <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
                            {title} ({data.length})
                        </Typography>
                    </Box>
                    <Tooltip title={`Download PDF (${title})`}>
                        <IconButton
                            onClick={() => handleDownloadPDF(data, title)}
                            sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
                        >
                            <PictureAsPdfIcon />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Box sx={{ maxHeight: 380, overflowY: 'auto', padding: 2 }}>
                    {data.length === 0 ? (
                        <Typography variant="body1" sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                            No hearings found.
                        </Typography>
                    ) : (
                        data.map((item) => (
                            <Card key={item.id || Math.random()} sx={{ mb: 2, boxShadow: '0px 2px 5px rgba(0,0,0,0.05)', borderRadius: 1.5 }}>
                                <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                            Case No: {item.case_no || 'N/A'}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                backgroundColor: item.hearing_status === 'Pending' ? '#fff3e0' : '#e8f5e9',
                                                color: item.hearing_status === 'Pending' ? '#e65100' : '#2e7d32',
                                                fontWeight: 'bold',
                                                px: 1,
                                                py: 0.5,
                                                borderRadius: 1
                                            }}
                                        >
                                            {item.hearing_status || 'Pending'}
                                        </Typography>
                                    </Box>

                                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: 'text.primary' }}>
                                        FIR NO: {item.fir_no || 'N/A'}
                                    </Typography>

                                    <Divider sx={{ my: 1 }} />

                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                        <strong>{isToday ? 'Hearing Date:' : 'Next Hearing Date:'}</strong>{' '}
                                        {item.next_hearing_date || item.hearing_date || 'N/A'}
                                    </Typography>

                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                        <strong>Court Name:</strong> {item.court_name || 'N/A'} (Room: {item.court_room || 'N/A'})
                                    </Typography>

                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                        <strong>Judge:</strong> {item.judge_name || 'N/A'}
                                    </Typography>

                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                        <strong>Opposite Party:</strong> {item.opposite_party || 'N/A'}
                                    </Typography>

                                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                                        <strong>Clients:</strong> {item.client_names && item.client_names.length > 0 ? item.client_names.join(', ') : 'N/A'}
                                    </Typography>
                                </CardContent>
                            </Card>
                        ))
                    )}
                </Box>
            </Box>
        );
    };

    return (
        <Box sx={{ padding: 3 }}>
            <Grid container spacing={3} alignItems="flex-start">

                {/* LEFT SIDE: Interactive Calendar with Counts */}
                <Grid item xs={12} md={6} lg={6}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 1.5, sm: 2.5 }, // Dynamic Padding
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: '#ffffff',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
                            // overflow: 'hidden'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                            <CalendarMonthIcon sx={{ color: 'primary.main', fontSize: 22 }} />
                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', letterSpacing: 0.3 }}>
                                Hearing Calendar
                            </Typography>
                        </Box>

                        <Divider sx={{ mb: 1.5 }} />

                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateCalendar
                                value={selectedDate}
                                onChange={(newValue) => {
                                    setSelectedDate(newValue);
                                    if (onDateSelect) onDateSelect(newValue.format('YYYY-MM-DD'));
                                }}
                                onMonthChange={handleMonthChange}
                                slots={{ day: ServerDay }}
                                sx={{
                                    width: '100%',
                                    maxHeight: 'auto',
                                    // Full Calendar Responsive Grid Overrides
                                    '& .MuiPickersCalendarHeader-root': {
                                        px: 1,
                                        pl: 2
                                    },
                                    '& .MuiPickersCalendarHeader-label': {
                                        fontWeight: 700,
                                        color: 'text.primary',
                                        fontSize: { xs: '0.95rem', sm: '1.1rem' }
                                    },
                                    '& .MuiDayCalendar-header': {
                                        justifyContent: 'space-between',
                                        mb: 1
                                    },
                                    '& .MuiDayCalendar-weekDayLabel': {
                                        flex: 1,
                                        width: 'auto',
                                        fontWeight: 700,
                                        color: 'text.secondary',
                                        fontSize: { xs: '0.75rem', sm: '0.85rem' }
                                    },
                                    '& .MuiDayCalendar-slideTransition': {
                                        minHeight: { xs: 260, sm: 310, md: 330 }, // Dynamic grid container height
                                        overflowX: 'hidden'
                                    },
                                    '& .MuiDayCalendar-monthContainer': {
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '2px'
                                    },
                                    '& .MuiDayCalendar-weekContainer': {
                                        margin: 0,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        width: '100%'
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </Paper>
                </Grid>

                {/* RIGHT SIDE: Today Hearings (Top) & Upcoming Hearings (Bottom) */}
                <Grid item xs={12} md={6} lg={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* 1. Today Hearing List (Upper) */}
                        <NoticeBoard
                            title={`${new Date(selectedDate).toLocaleDateString("en-GB", {
                                weekday: "short",
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                            })} Hearing List`}
                            data={todayHearingList}
                            isToday={true}
                        />

                        {/* 2. Upcoming Hearing List (Lower) */}
                        {/* <NoticeBoard
                            title="Upcoming Hearing List"
                            data={upcomingHearings}
                            isToday={false}
                        /> */}
                    </Box>
                </Grid>

            </Grid>
        </Box>
    );
};

export default HearingDetails;